import { reactive } from 'vue';
import type { BundleInfo } from '@capgo/capacitor-updater';

export type NativeUpdatePhase = 'idle' | 'downloading' | 'ready' | 'applying';
const automaticAttemptKey = 'eqover-ota-automatic-attempt-v1';

export function createNativeUpdateController(dependencies: {
  current: () => Promise<{ bundle: BundleInfo; native?: string }>;
  set: (options: { id: string }) => Promise<void>;
  storage: () => Pick<Storage, 'getItem' | 'setItem'>;
  blocked?: (version: string) => boolean;
}) {
  const state = reactive({ phase: 'idle' as NativeUpdatePhase, percent: 0, version: '', bundleId: '' });
  let attemptedVersion = '';

  function clear() {
    Object.assign(state, { phase: 'idle', percent: 0, version: '', bundleId: '' });
  }

  function isApplying() { return state.phase === 'applying'; }
  function acceptsProgress() { return state.phase !== 'applying' && state.phase !== 'ready'; }

  async function loadedBundle() {
    const current = await dependencies.current();
    return current.bundle.id === 'builtin' && current.native
      ? { ...current.bundle, version: current.native }
      : current.bundle;
  }

  function isCandidate(bundle: Pick<BundleInfo, 'id' | 'version' | 'status'>, current: BundleInfo) {
    return Boolean(bundle.id && bundle.id !== 'builtin' && bundle.version.trim())
      && ['pending', 'success'].includes(bundle.status)
      && bundle.id !== current.id
      && bundle.version.trim() !== current.version.trim();
  }

  async function ready(bundle: BundleInfo) {
    if (isApplying() || dependencies.blocked?.(bundle.version)) return;
    const current = await loadedBundle();
    if (isApplying() || dependencies.blocked?.(bundle.version)) return;
    if (!isCandidate(bundle, current)) {
      if (state.bundleId === bundle.id) clear();
      return;
    }
    Object.assign(state, { phase: 'ready', percent: 100, version: bundle.version.trim(), bundleId: bundle.id });
  }

  async function progress(percent: number, bundle: BundleInfo) {
    if (!acceptsProgress() || dependencies.blocked?.(bundle.version)) return;
    const current = await loadedBundle();
    if (!acceptsProgress() || dependencies.blocked?.(bundle.version)) return;
    if (bundle.id === current.id || bundle.version.trim() === current.version.trim()) return;
    Object.assign(state, {
      phase: 'downloading', percent: Math.max(0, Math.min(100, Math.round(percent))),
      version: bundle.version.trim(), bundleId: bundle.id,
    });
  }

  function failed(version: string) {
    if (state.phase === 'applying' || (state.version && state.version !== version)) return;
    clear();
  }

  async function apply(automatic = false) {
    if (state.phase !== 'ready' || !state.bundleId || dependencies.blocked?.(state.version)) return;
    const bundle = { id: state.bundleId, version: state.version, status: 'pending' as const };
    // Bloquear también durante la validación evita dos set() simultáneos.
    state.phase = 'applying';
    try {
      const current = await loadedBundle();
      if (dependencies.blocked?.(bundle.version)) { clear(); return; }
      if (!isCandidate(bundle, current)) {
        clear();
        return;
      }
      if (automatic) {
        // Persistir ANTES de set(): el contexto JS desaparece al recargar, incluso si hay rollback.
        // Si el almacenamiento falla, dejar disponible la acción manual.
        const storage = dependencies.storage();
        if (attemptedVersion === bundle.version || storage.getItem(automaticAttemptKey) === bundle.version) {
          state.phase = 'ready';
          return;
        }
        storage.setItem(automaticAttemptKey, bundle.version);
        attemptedVersion = bundle.version;
      }
      await dependencies.set({ id: bundle.id });
    } catch (error) {
      state.phase = 'ready';
      throw error;
    }
  }

  return { state, ready, progress, failed, apply, clear };
}
